import { useMemo } from 'react';

/**
 * Minimal subset of a `useQueries` row we need to build the fingerprint.
 * `dataUpdatedAt` + length is enough to detect a real data change without
 * holding a reference to the whole result-array (which changes identity on
 * every render).
 */
interface FingerprintableQuery {
  dataUpdatedAt: number;
  data?: unknown;
}

/**
 * Build a positional fingerprint string for a list of `useQueries` results.
 * The fingerprint composes each row's `dataUpdatedAt` with a caller-provided
 * length count; together they identify a "data changed" event for a
 * react-query result without retaining the result-array's identity.
 *
 * Pure function — extracted from `useStableFold` so the fingerprint
 * behaviour can be unit-tested without React.
 *
 * NOTE: this relies on TanStack Query bumping `dataUpdatedAt` on every
 * refetch — even when the payload is identical. If that ever stops being
 * true, content-only changes (same length, different field values) would
 * be missed and the memoised fold would return a stale value.
 */
export function buildQueriesFingerprint<Q extends FingerprintableQuery>(
  queries: Q[],
  getLength: (q: Q) => number,
): string {
  return queries.map(q => `${q.dataUpdatedAt}:${getLength(q)}`).join('|');
}

/**
 * Fold the result of `useQueries` into a stable value, recomputed only when
 * actual query data changes. Replaces the duplicated `dataUpdatedAt:length`
 * fingerprint pattern in `useProgramCases` / `useWorkflowSlaMap`.
 *
 * The fold function itself is intentionally NOT a dep of the inner memo —
 * the fingerprint is the stable signal. Callers should keep the fold pure
 * of external state.
 *
 * See `buildQueriesFingerprint` for the caveat about TanStack Query's
 * `dataUpdatedAt` semantics.
 */
export function useStableFold<Q extends FingerprintableQuery, T>(
  queries: Q[],
  getLength: (q: Q) => number,
  fold: (queries: Q[]) => T,
): T {
  const fingerprint = buildQueriesFingerprint(queries, getLength);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => fold(queries), [fingerprint]);
}
